import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ExternalLink, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BookCallPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-8">
              <Calendar className="w-8 h-8 text-primary" />
            </div>
            <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Book a Call</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Let's Discuss Your{' '}
              <span className="text-gradient">Growth Strategy</span>
            </h1>
            <p className="text-fp-textMuted text-lg leading-relaxed max-w-2xl mx-auto">
              Schedule a free 30-minute strategy call with our team. We'll discuss your goals, 
              challenges, and explore how we can help accelerate your growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calendly Section */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-card rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Choose a Time That Works</h2>
              <p className="text-fp-textMuted">
                Click below to view available slots and book your free strategy call.
              </p>
            </div>
            
            <a
              href="https://calendly.com/focuspixel/new-meeting"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3 text-lg px-8 py-4"
              data-testid="book-call-calendly-btn"
            >
              <Calendar className="w-5 h-5" />
              Schedule on Calendly
              <ExternalLink className="w-4 h-4" />
            </a>

            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-fp-textMuted text-sm mb-4">What to expect:</p>
              <div className="flex flex-wrap justify-center gap-4">
                {['30-min call', 'No commitment', 'Custom insights'].map((item, i) => (
                  <span key={i} className="px-4 py-2 bg-white/5 rounded-full text-sm text-white/70">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Alternative Contact */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <p className="text-fp-textMuted mb-6">Prefer a different way to connect?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/917030771071"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="book-call-whatsapp-btn"
              >
                WhatsApp Us
              </a>
              <a
                href="mailto:hello@focuspixel.in"
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="book-call-email-btn"
              >
                Send an Email
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ / What We'll Discuss */}
      <section className="section-padding bg-fp-surface/30">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
              What We'll Cover
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Your Current Situation', desc: 'We\'ll understand where you are today and what\'s working (or not).' },
                { title: 'Your Goals', desc: 'What does success look like for you? We\'ll get specific about outcomes.' },
                { title: 'Growth Opportunities', desc: 'We\'ll identify quick wins and strategic opportunities.' },
                { title: 'Recommended Next Steps', desc: 'You\'ll leave with a clear action plan, regardless of whether we work together.' },
              ].map((item, index) => (
                <div key={index} className="glass-card p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-fp-textMuted text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookCallPage;
